// FEATURES-1 (docs/specs/features-1.md): the admin portal's runtime feature switches.
//
// Two layers per feature, and only one of them lives here:
//   - Infrastructure (Deployed / Needs approval) is a deploy-time AppConfig flag the maintainer
//     approves. Read-only here; "Request infrastructure" opens a prefilled GitHub issue for the
//     maintainer, client-side (a plain link, no server egress, no personal data in the body).
//   - Runtime (the toggle) is what an admin switches. Disabled, with the reason, until the
//     infrastructure exists. Features with a cost or behaviour note confirm before switching on.
//
// Everything shown comes from `GET /admin/features`; the cost column is static registry text and
// is labelled as an estimate.
import { Fragment, useId, useState } from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import type { AdminFeature, FeatureSwitchChange } from '@scribe/core/types';
import { infraRequestUrl } from '../lib/infraRequest';
import { useAdminFeatures, useUpdateFeatureSwitch } from '../lib/queries';
import { Badge, Modal, formatDate } from './ui';
import { BugReportsPanel } from './BugReportsPanel';

/** The feature whose row carries the bug reports list (delivery to GitHub is parked). */
const BUG_REPORTS_KEY = 'bug_reports';

function describeChange(feature: AdminFeature, change: FeatureSwitchChange): string {
  const who = change.actor_display_name ?? 'an admin';
  return `${feature.name} turned ${change.enabled ? 'on' : 'off'} by ${who} · ${formatDate(change.at)}`;
}

function latestChange(features: AdminFeature[]): { feature: AdminFeature; change: FeatureSwitchChange } | null {
  let best: { feature: AdminFeature; change: FeatureSwitchChange } | null = null;
  for (const feature of features) {
    const change = feature.last_change;
    if (change && (!best || change.at > best.change.at)) best = { feature, change };
  }
  return best;
}

export function FeaturesCard({ isAdmin }: { isAdmin: boolean }) {
  const titleId = useId();
  const query = useAdminFeatures(isAdmin);
  const update = useUpdateFeatureSwitch();
  const [confirming, setConfirming] = useState<AdminFeature | null>(null);
  const [showReports, setShowReports] = useState(false);

  const features = query.data?.features ?? [];
  const waiting = features.filter((f) => f.infrastructure !== 'deployed');
  const last = latestChange(features);

  function toggle(feature: AdminFeature) {
    const next = !feature.enabled;
    if (next && feature.confirm_note) {
      setConfirming(feature);
      return;
    }
    update.mutate({ key: feature.key, enabled: next });
  }

  return (
    <section className="row-secondary features-card" aria-labelledby={titleId}>
      <h2 id={titleId}>Features</h2>
      <p className="muted">
        Switch features on or off for everyone. Changes reach every server within about a minute. Infrastructure is
        set up by the maintainer and can't be changed here.
      </p>

      {query.isLoading && <p className="muted">Loading features…</p>}
      {query.isError && (
        <div className="banner banner-danger">
          <p>{query.error instanceof Error && query.error.message ? query.error.message : 'Failed to load features.'}</p>
          <button className="sm" onClick={() => void query.refetch()}>
            Retry
          </button>
        </div>
      )}

      {query.data && (
        <div className="table-wrap">
          <table className="table features-table">
            <thead>
              <tr>
                <th scope="col">Feature</th>
                <th scope="col">Infrastructure</th>
                <th scope="col">
                  Est. cost/mo <span className="muted">(estimate)</span>
                </th>
                <th scope="col">
                  <span className="sr-only">On or off</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {features.length === 0 && (
                <tr>
                  <td className="empty" colSpan={4}>
                    No switchable features.
                  </td>
                </tr>
              )}
              {features.map((f) => {
                const reportsRow = f.key === BUG_REPORTS_KEY && f.infrastructure === 'deployed' && f.enabled;
                return (
                  <Fragment key={f.key}>
                    <FeatureRow
                      feature={f}
                      pending={update.isPending && update.variables?.key === f.key}
                      onToggle={() => toggle(f)}
                      reports={reportsRow ? { open: showReports, onToggle: () => setShowReports((v) => !v) } : undefined}
                    />
                    {reportsRow && showReports && (
                      <tr className="features-subrow">
                        <td colSpan={4}>
                          <BugReportsPanel />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {update.isError && <p className="error">{update.error.message || 'Could not change the feature.'}</p>}

      {query.data && (
        <div className="features-footer">
          <span className="muted">{last ? `Last change: ${describeChange(last.feature, last.change)}` : 'No changes yet.'}</span>
          {waiting.length > 0 && (
            <a
              className="button-link ghost sm"
              href={infraRequestUrl(waiting, typeof window !== 'undefined' ? window.location.host : '')}
              target="_blank"
              rel="noopener noreferrer"
            >
              Request infrastructure
              <ArrowTopRightOnSquareIcon width={16} height={16} aria-hidden="true" />
              <span className="sr-only">(opens GitHub in a new tab)</span>
            </a>
          )}
        </div>
      )}

      {confirming && (
        <ConfirmEnable
          feature={confirming}
          pending={update.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={(reason) => {
            update.mutate(
              reason ? { key: confirming.key, enabled: true, reason } : { key: confirming.key, enabled: true },
              { onSettled: () => setConfirming(null) },
            );
          }}
        />
      )}
    </section>
  );
}

function FeatureRow({
  feature,
  pending,
  onToggle,
  reports,
}: {
  feature: AdminFeature;
  pending: boolean;
  onToggle: () => void;
  /** Bug reports only: the "Show reports" control for the list under this row. */
  reports?: { open: boolean; onToggle: () => void } | undefined;
}) {
  const reasonId = useId();
  const deployed = feature.infrastructure === 'deployed';
  return (
    <tr>
      <td>
        <div className="features-name">{feature.name}</div>
        <div className="muted">{feature.description}</div>
        {reports && (
          <button type="button" className="ghost sm" aria-expanded={reports.open} onClick={reports.onToggle}>
            {reports.open ? 'Hide reports' : 'Show reports'}
          </button>
        )}
      </td>
      <td>
        {deployed ? <Badge kind="success">Deployed</Badge> : <Badge kind="pending">Needs approval</Badge>}
      </td>
      <td className="features-cost">{feature.cost_estimate}</td>
      <td>
        <button
          type="button"
          role="switch"
          className="switch"
          aria-checked={feature.enabled}
          aria-label={`${feature.name}: ${feature.enabled ? 'on' : 'off'}`}
          aria-describedby={deployed ? undefined : reasonId}
          disabled={!deployed || pending}
          onClick={onToggle}
        >
          <span className="switch-thumb" aria-hidden="true" />
        </button>
        {!deployed && (
          <div id={reasonId} className="features-reason muted">
            Needs infrastructure first
          </div>
        )}
      </td>
    </tr>
  );
}

function ConfirmEnable({
  feature,
  pending,
  onCancel,
  onConfirm,
}: {
  feature: AdminFeature;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const titleId = useId();
  const [reason, setReason] = useState('');
  return (
    <Modal onClose={onCancel} labelledBy={titleId}>
      <h2 id={titleId}>Turn on {feature.name}?</h2>
      <p>{feature.confirm_note}</p>
      <p className="muted">Estimated cost: {feature.cost_estimate}</p>
      <div className="field">
        <span className="label">Reason (optional, kept in the audit log)</span>
        <input
          aria-label="Reason"
          maxLength={500}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
      <div className="row">
        <button disabled={pending} onClick={() => onConfirm(reason.trim())}>
          {pending ? 'Turning on…' : 'Turn on'}
        </button>
        <button className="ghost" disabled={pending} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}
